import {
  ChangeEvent,
  Dispatch,
  Reducer,
  useCallback,
  useEffect,
  useReducer,
} from 'react';
import { DocumentReference, getDoc, setDoc } from 'firebase/firestore';
import {
  fetchArticleEventNotification,
  FirebaseUserNotification,
  updateArticleEventNotification,
} from '../repository/NotificationRepository';
import { useCurrentFirebaseUserRef } from '../../firebase/useCollection';
import {
  useAnalytics,
  METRICS_ARTICLES_NOTIFICATIONS,
  METRICS_EVENTS_NOTIFICATIONS,
  METRICS_CARE_TEAM_NOTIFICATIONS,
  METRICS_COMMUNITY_NOTIFICATIONS,
  METRICS_ANNOUNCEMENTS_NOTIFICATIONS,
} from '@xolv-mybrightlink/common';

type UserNotificationSetting = {
  announcementNotificationEnabled: boolean;
  careTeamNotificationEnabled: boolean;
  communityNotificationEnabled: boolean;
  articleNotificationEnabled: boolean;
  eventNotificationEnabled: boolean;
};

const DEFAULT_USER_NOTIFICATION_SETTING = {
  announcementNotificationEnabled: true,
  careTeamNotificationEnabled: true,
  communityNotificationEnabled: true,
  articleNotificationEnabled: true,
  eventNotificationEnabled: true,
};

type FetchAccountNotificationSetting = {
  type: 'FETCH_ACCOUNT_NOTIFICATION_SETTING';
  payload: UserNotificationSetting;
};

type ToggleAnnouncementNotification = {
  type: 'TOGGLE_ANNOUNCEMENT_NOTIFICATION';
  payload: boolean;
};

type ToggleCareTeamNotification = {
  type: 'TOGGLE_CARETEAM_NOTIFICATION';
  payload: boolean;
};

type ToggleCommunityNotification = {
  type: 'TOGGLE_COMMUNITY_NOTIFICATION';
  payload: boolean;
};

type ToggleArticleNotification = {
  type: 'TOGGLE_ARTICLE_NOTIFICATION';
  payload: boolean;
};

type ToggleEventNotification = {
  type: 'TOGGLE_EVENT_NOTIFICATION';
  payload: boolean;
};

type Action =
  | FetchAccountNotificationSetting
  | ToggleAnnouncementNotification
  | ToggleCareTeamNotification
  | ToggleCommunityNotification
  | ToggleArticleNotification
  | ToggleEventNotification;

export const useAccountNotificationSetting = () => {
  const analytics = useAnalytics();
  const currentFirebaseUserRef = useCurrentFirebaseUserRef();
  const [state, dispatch] = useReducer<
    Reducer<UserNotificationSetting, Action>
  >(reducer, DEFAULT_USER_NOTIFICATION_SETTING);
  useFetchNotification(dispatch, currentFirebaseUserRef);

  const handleToggleAnnouncementNotification = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const announcementNotificationEnabled = event.target.checked;
      await setDoc(
        currentFirebaseUserRef,
        { announcementNotificationEnabled },
        { merge: true },
      );
      dispatch({
        type: 'TOGGLE_ANNOUNCEMENT_NOTIFICATION',
        payload: announcementNotificationEnabled,
      });
      analytics.updateUserProperty(
        METRICS_ANNOUNCEMENTS_NOTIFICATIONS,
        announcementNotificationEnabled,
      );
    },
    [currentFirebaseUserRef, dispatch, analytics],
  );

  const handleToggleCareTeamNotification = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const careTeamNotificationEnabled = event.target.checked;
      await setDoc(
        currentFirebaseUserRef,
        { careTeamNotificationEnabled },
        { merge: true },
      );
      dispatch({
        type: 'TOGGLE_CARETEAM_NOTIFICATION',
        payload: careTeamNotificationEnabled,
      });
      analytics.updateUserProperty(
        METRICS_CARE_TEAM_NOTIFICATIONS,
        careTeamNotificationEnabled,
      );
    },
    [currentFirebaseUserRef, dispatch, analytics],
  );

  const handleToggleCommunityNotification = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const communityNotificationEnabled = event.target.checked;
      await setDoc(
        currentFirebaseUserRef,
        { communityNotificationEnabled },
        { merge: true },
      );
      dispatch({
        type: 'TOGGLE_COMMUNITY_NOTIFICATION',
        payload: communityNotificationEnabled,
      });
      analytics.updateUserProperty(
        METRICS_COMMUNITY_NOTIFICATIONS,
        communityNotificationEnabled,
      );
    },
    [currentFirebaseUserRef, dispatch, analytics],
  );

  const handleToggleArticleNotification = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const articleNotificationEnabled = event.target.checked;
      await updateArticleEventNotification(
        articleNotificationEnabled,
        state.eventNotificationEnabled,
      );
      dispatch({
        type: 'TOGGLE_ARTICLE_NOTIFICATION',
        payload: articleNotificationEnabled,
      });
      analytics.updateUserProperty(
        METRICS_ARTICLES_NOTIFICATIONS,
        articleNotificationEnabled,
      );
    },
    [dispatch, state.eventNotificationEnabled, analytics],
  );

  const handleToggleEventNotification = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const eventNotificationEnabled = event.target.checked;
      await updateArticleEventNotification(
        state.articleNotificationEnabled,
        eventNotificationEnabled,
      );
      dispatch({
        type: 'TOGGLE_EVENT_NOTIFICATION',
        payload: eventNotificationEnabled,
      });
      analytics.updateUserProperty(
        METRICS_EVENTS_NOTIFICATIONS,
        eventNotificationEnabled,
      );
    },
    [dispatch, state.articleNotificationEnabled, analytics],
  );

  const eventHandler = {
    handleToggleAnnouncementNotification,
    handleToggleCareTeamNotification,
    handleToggleCommunityNotification,
    handleToggleArticleNotification,
    handleToggleEventNotification,
  };

  return {
    state,
    dispatch,
    eventHandler,
  };
};

const reducer = (
  currentState: Readonly<UserNotificationSetting>,
  action: Action,
): Readonly<UserNotificationSetting> => {
  switch (action.type) {
    case 'FETCH_ACCOUNT_NOTIFICATION_SETTING':
      return setAccountNotification(currentState, action.payload);
    case 'TOGGLE_ANNOUNCEMENT_NOTIFICATION':
      return toggleAnnouncementNotification(currentState, action.payload);
    case 'TOGGLE_CARETEAM_NOTIFICATION':
      return toggleCareTeamNotification(currentState, action.payload);
    case 'TOGGLE_COMMUNITY_NOTIFICATION':
      return toggleCommunityNotification(currentState, action.payload);
    case 'TOGGLE_ARTICLE_NOTIFICATION':
      return toggleArticleNotification(currentState, action.payload);
    case 'TOGGLE_EVENT_NOTIFICATION':
      return toggleEventNotification(currentState, action.payload);
    default:
      throw new Error(`Unsupported action type ${action}`);
  }
};

const setAccountNotification = (
  currentState: Readonly<UserNotificationSetting>,
  fetchedNotificationSetting: UserNotificationSetting,
) => {
  return { ...currentState, ...fetchedNotificationSetting };
};

const toggleAnnouncementNotification = (
  currentState: Readonly<UserNotificationSetting>,
  value: boolean,
) => {
  return {
    ...currentState,
    announcementNotificationEnabled: value,
  };
};

const toggleCareTeamNotification = (
  currentState: Readonly<UserNotificationSetting>,
  value: boolean,
) => {
  return {
    ...currentState,
    careTeamNotificationEnabled: value,
  };
};

const toggleCommunityNotification = (
  currentState: Readonly<UserNotificationSetting>,
  value: boolean,
) => {
  return {
    ...currentState,
    communityNotificationEnabled: value,
  };
};

const toggleArticleNotification = (
  currentState: Readonly<UserNotificationSetting>,
  value: boolean,
) => {
  return {
    ...currentState,
    articleNotificationEnabled: value,
  };
};

const toggleEventNotification = (
  currentState: Readonly<UserNotificationSetting>,
  value: boolean,
) => {
  return {
    ...currentState,
    eventNotificationEnabled: value,
  };
};

const useFetchNotification = (
  dispatch: Dispatch<Action>,
  firebaseUserDocumentRef: DocumentReference,
) => {
  useEffect(() => {
    const fetchNotification = async () => {
      const documentData = await getDoc(firebaseUserDocumentRef);
      const fbNotification = documentData.data() as FirebaseUserNotification;
      const {
        forArticles: articleNotificationEnabled,
        forEvents: eventNotificationEnabled,
      } = await fetchArticleEventNotification();

      const fetchedNotificationSetting = {
        announcementNotificationEnabled:
          fbNotification.announcementNotificationEnabled == null ||
          fbNotification.announcementNotificationEnabled,
        careTeamNotificationEnabled:
          fbNotification.careTeamNotificationEnabled == null ||
          fbNotification.careTeamNotificationEnabled,
        communityNotificationEnabled:
          fbNotification.communityNotificationEnabled == null ||
          fbNotification.communityNotificationEnabled,
        articleNotificationEnabled,
        eventNotificationEnabled,
      };

      dispatch({
        type: 'FETCH_ACCOUNT_NOTIFICATION_SETTING',
        payload: fetchedNotificationSetting,
      });
    };
    fetchNotification();
  }, [firebaseUserDocumentRef, dispatch]);
};
